/**
 * @file        src/pages/wallet/OutstandingDues.jsx
 * @description Wallet Management → Outstanding Dues. Who owes us money, how
 *              much, and for how long.
 *
 *              Replaces the old Account Management → Credit Dues screen, which
 *              listed the retired B2B credit line's per-booking `credit_dues`
 *              rows (client decision 2026-10-06, WALLET_DUES_SPEC.md). Three
 *              things about this screen follow from that decision and are not
 *              cosmetic:
 *
 *              ONE ROW PER WALLET, NOT PER BOOKING. A customer has one debt.
 *              Nothing allocates a payment to a particular booking, so
 *              per-booking rows with their own settled amounts would be showing
 *              an allocation we do not make. The bookings that created the debt
 *              are detail, shown when a row is opened.
 *
 *              RETAIL AND CORPORATE IN ONE QUEUE. Credit is no longer a B2B
 *              facility, so "who" is a filterable COLUMN here rather than two
 *              screens — the shape Abandoned Payments settled on (CLAUDE.md
 *              §16), and the deliberate exception to the §9d split.
 *
 *              AGE, NOT OVERDUE. Due Days is gone and a due has no deadline;
 *              the grant's expiry is the only date in the system. So the queue
 *              sorts oldest-first and the column reads "Outstanding since".
 *              There is no overdue state to filter on because there is no date
 *              to be late against.
 *
 *              Settling re-verifies the admin's password, exactly like a wallet
 *              credit or refund — claiming a debt was paid is as consequential
 *              as crediting one, and writing one off more so.
 * @author      Kinetika Labs
 * @created     2026-10-06
 */
import { useState } from 'react';
import ReadOnlyListPage from '../flight/ReadOnlyListPage';
import { DUES_NOTE } from '../../lib/pricingNotes';
import Chip from '../../components/Chip';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import SettleDueModal from './SettleDueModal';

const inr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function day(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(s);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** How long money has been outstanding — the signal that replaced "overdue". */
function ageDays(s) {
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
}

export default function OutstandingDues() {
  const [ownerType, setOwnerType] = useState('');
  const [view, setView] = useState('');
  const [settleRow, setSettleRow] = useState(null);

  const toolbar = (
    <div className="filter-row">
      <div className="field" style={{ minWidth: 180 }}>
        <label>Who</label>
        <Select
          value={ownerType}
          onChange={setOwnerType}
          placeholder="Everyone"
          options={[
            { value: '', label: 'Everyone' },
            { value: 'business', label: 'Corporate accounts' },
            { value: 'user', label: 'Retail customers' },
          ]}
        />
      </div>
      <div className="field" style={{ minWidth: 180 }}>
        <label>Show</label>
        <Select
          value={view}
          onChange={setView}
          placeholder="Still outstanding"
          options={[
            { value: '', label: 'Still outstanding' },
            { value: 'settled', label: 'Settled' },
            { value: 'all', label: 'All with any due history' },
          ]}
        />
      </div>
    </div>
  );

  const extraParams = {
    ...(ownerType ? { ownerType } : {}),
    ...(view ? { view } : {}),
  };

  const rowActions = (row) => (
    Number(row.dues) > 0 ? (
      <button className="btn xs primary" onClick={() => setSettleRow(row)}>
        <Icon name="check" /> Settle
      </button>
    ) : <span className="muted">—</span>
  );

  return (
    <>
      <ReadOnlyListPage
        title="Outstanding Dues"
        subtitle="Wallet Management · credit spent and not yet repaid"
        notice={DUES_NOTE}
        endpoint="/credit-dues"
        queryRoot="credit-dues"
        searchLabel="Name / Email"
        actionsLabel="Action"
        toolbar={toolbar}
        rowActions={rowActions}
        extraParams={Object.keys(extraParams).length ? extraParams : undefined}
        columns={[
          {
            key: 'ownerName',
            label: 'Customer',
            render: (v, r) => (
              <div>
                <b>{v}</b>
                <div className="muted" style={{ fontSize: '.8em' }}>{r.ownerEmail || '—'}</div>
              </div>
            ),
          },
          {
            key: 'ownerType',
            label: 'Type',
            render: (v) => (
              <Chip tone={v === 'business' ? 'pend' : 'ok'}>
                {v === 'business' ? 'Corporate' : 'Retail'}
              </Chip>
            ),
          },
          { key: 'brand', label: 'Brand', render: (v) => v || <span className="muted">Unknown</span> },
          { key: 'dues', label: 'Outstanding', align: 'right', render: (v) => <b>{inr(v)}</b> },
          { key: 'balance', label: 'Spendable', align: 'right', render: inr },
          {
            key: 'net',
            label: 'Net',
            align: 'right',
            // The figure the CUSTOMER sees on their own wallet, which is the one
            // support will be asked about. It is the only number here that can
            // be negative, and it is coloured so that is never a surprise.
            render: (v) => (
              <b style={Number(v) < 0 ? { color: 'var(--rose)' } : undefined}>{inr(v)}</b>
            ),
          },
          {
            key: 'since',
            label: 'Outstanding since',
            render: (v) => {
              const n = ageDays(v);
              return (
                <div>
                  <div>{day(v)}</div>
                  {n !== null ? (
                    <div className="muted" style={{ fontSize: '.8em' }}>
                      {n === 0 ? 'today' : `${n} day${n === 1 ? '' : 's'}`}
                    </div>
                  ) : null}
                </div>
              );
            },
          },
        ]}
      />
      {settleRow ? <SettleDueModal row={settleRow} onClose={() => setSettleRow(null)} /> : null}
    </>
  );
}
